/**
 * ConnectionStatus - Displays encryption state, connection quality, and call duration
 * Required for BMV-Ä compliance (E2E encryption indicator)
 */

import * as React from "react";
import { cn } from "../../lib/utils";
import { Lock, LockOpen, Loader2 } from "lucide-react";
import type { ConnectionStatus as ConnectionState, ConnectionQualityLevel } from "./hooks/useVideoRoom";

export interface ConnectionStatusLabels {
  encryptionActive?: string;
  notEncrypted?: string;
  connecting?: string;
  reconnecting?: string;
  disconnected?: string;
  qualityExcellent?: string;
  qualityGood?: string;
  qualityPoor?: string;
  connected?: string;
}

const DEFAULT_LABELS: Required<ConnectionStatusLabels> = {
  encryptionActive: "Encryption active",
  notEncrypted: "Not encrypted",
  connecting: "Connecting...",
  reconnecting: "Reconnecting...",
  disconnected: "Disconnected",
  qualityExcellent: "Excellent",
  qualityGood: "Good",
  qualityPoor: "Poor",
  connected: "Connected",
};

export interface ConnectionStatusProps {
  status: ConnectionState;
  connectionQuality?: ConnectionQualityLevel;
  startTime?: number;
  /** Whether E2E encryption is currently active */
  isE2EEEnabled?: boolean;
  /** Translated labels for all displayed text */
  labels?: ConnectionStatusLabels;
  className?: string;
}

export function ConnectionStatus({
  status,
  connectionQuality = "unknown",
  startTime,
  isE2EEEnabled = false,
  labels: labelsProp,
  className,
}: ConnectionStatusProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [duration, setDuration] = React.useState("00:00:00");

  // Update duration every second
  React.useEffect(() => {
    if (!startTime || status !== "connected") {
      setDuration("00:00:00");
      return;
    }

    const updateDuration = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      const seconds = elapsed % 60;
      setDuration(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [startTime, status]);

  const getQualityDisplay = () => {
    if (status === "connecting") {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: labels.connecting,
        color: "text-amber-500",
        bars: 0,
      };
    }
    if (status === "reconnecting") {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: labels.reconnecting,
        color: "text-amber-500",
        bars: 0,
      };
    }
    if (status === "disconnected") {
      return {
        icon: null,
        text: labels.disconnected,
        color: "text-red-500",
        bars: 0,
      };
    }

    // Connected - show quality
    switch (connectionQuality) {
      case "excellent":
        return {
          icon: null,
          text: labels.qualityExcellent,
          color: "text-green-500",
          bars: 3,
        };
      case "good":
        return {
          icon: null,
          text: labels.qualityGood,
          color: "text-green-500",
          bars: 2,
        };
      case "poor":
        return {
          icon: null,
          text: labels.qualityPoor,
          color: "text-amber-500",
          bars: 1,
        };
      default:
        return {
          icon: null,
          text: labels.connected,
          color: "text-green-500",
          bars: 0,
        };
    }
  };

  const qualityDisplay = getQualityDisplay();

  return (
    <div
      className={cn(
        "flex items-center gap-4 px-4 py-2 bg-card/80 backdrop-blur rounded-full",
        className
      )}
    >
      {/* E2E Encryption indicator - REQUIRED for compliance */}
      {isE2EEEnabled ? (
        <div className="flex items-center gap-1.5 text-sage-600">
          <Lock className="w-4 h-4" />
          <span className="text-sm font-medium">{labels.encryptionActive}</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-amber-600">
          <LockOpen className="w-4 h-4" />
          <span className="text-sm font-medium">{labels.notEncrypted}</span>
        </div>
      )}

      <div className="w-px h-4 bg-border" />

      {/* Connection quality */}
      <div className={cn("flex items-center gap-1.5", qualityDisplay.color)}>
        {qualityDisplay.icon}
        {qualityDisplay.bars > 0 && (
          <div className="flex items-end gap-0.5">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  "w-1 rounded-sm",
                  i === 1 ? "h-1.5" : i === 2 ? "h-2.5" : "h-3.5",
                  i <= qualityDisplay.bars
                    ? qualityDisplay.bars === 1
                      ? "bg-amber-500"
                      : "bg-green-500"
                    : "bg-muted-foreground/30"
                )}
              />
            ))}
          </div>
        )}
        <span className="text-sm">{qualityDisplay.text}</span>
      </div>

      <div className="w-px h-4 bg-border" />

      {/* Duration */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span className="text-sm font-mono">{duration}</span>
      </div>
    </div>
  );
}
